import { useEffect } from 'react'
import { SegmentedControl, Stack, Text, TextInput } from '@mantine/core'
import type { TaskArgsComponentProps } from '../args-registry'
import {
  buildFindingsSyncArgs,
  extractPatient,
  extractSince,
  sinceMode,
  type SinceMode,
} from './FindingsSyncArgs.utils'

/**
 * Args editor/viewer for findings.sync: { patient?, since? }. No args is the nightly case —
 * every patient, from each one's oldest report the current converter has not processed. A
 * start date reconverts every report dated on or after it (the run refuses while an older
 * report is unprocessed); 'all' reconverts the whole history.
 */
export function FindingsSyncArgs({ value, onChange, readOnly }: TaskArgsComponentProps) {
  const patient = extractPatient(value)
  const since = extractSince(value)
  const patientText = typeof patient === 'string' ? patient : ''
  const mode = sinceMode(since)

  // Edit mode opened with no args: seed the empty-object default so the dialog opens valid.
  useEffect(() => {
    if (!readOnly && (value === undefined || value === null)) {
      onChange?.({})
    }
  }, [readOnly, value, onChange])

  if (readOnly) {
    return (
      <Text size="sm">
        {mode === 'unprocessed' && (
          <>
            <b>Unprocessed reports</b> — from each patient's oldest unprocessed report (default)
          </>
        )}
        {mode === 'date' && (
          <>
            From <b>{String(since)}</b> — every report dated on or after it, reconverted
          </>
        )}
        {mode === 'all' && (
          <>
            <b>Entire history</b> — every report reconverted
          </>
        )}
        {patientText ? ` · patient ${patientText}` : ' · all patients'}
      </Text>
    )
  }

  const dateText = mode === 'date' && typeof since === 'string' ? since : ''
  const change = (nextMode: SinceMode, nextDate: string, nextPatient: string) =>
    onChange?.(
      buildFindingsSyncArgs(
        nextPatient,
        nextMode === 'unprocessed' ? undefined : nextMode === 'all' ? 'all' : nextDate,
      ),
    )

  return (
    <Stack gap="sm">
      <div>
        <Text size="sm" fw={500} mb={4}>
          Reports to convert
        </Text>
        <SegmentedControl
          aria-label="Reports to convert"
          value={mode}
          data={[
            { label: 'Unprocessed', value: 'unprocessed' },
            { label: 'From date', value: 'date' },
            { label: 'Entire history', value: 'all' },
          ]}
          onChange={(next) => change(next as SinceMode, dateText, patientText)}
        />
      </div>
      {mode === 'date' && (
        <TextInput
          type="date"
          label="Start date (UTC, inclusive)"
          description="Reconverts every report dated on or after this day. Refused while an older report is still unprocessed — the error names the latest date you can use."
          value={dateText}
          onChange={(event) => change('date', event.currentTarget.value, patientText)}
        />
      )}
      <TextInput
        label="Patient id"
        description="Convert one patient's reports only. Leave empty for every patient."
        placeholder="all patients"
        value={patientText}
        onChange={(event) => change(mode, dateText, event.currentTarget.value)}
      />
    </Stack>
  )
}
